// PreventiApp · Dashboard Layout
// Sprint 5 · Paso 2 · usa TanStack Query hook para eventos-centinela
// (fuente única) y expone el resultado via Outlet context.
// Sprint 5 · Paso 4 · muestra chip "Rol · Alcance" para reforzar diferenciación
// visual sin cambiar permisos ni RLS.
import React, { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { Bell, ShieldCheck, GraduationCap, ArrowLeft, HelpCircle } from 'lucide-react';
import { useEventosCentinela } from '../../../lib/dashboardQueries';
import { useAuth } from '../../../context/AuthContext';
import { track } from '../../../lib/metrics';
import PriorityCriteriaModal from './PriorityCriteriaModal';

const STORAGE_KEY = 'preventiapp.dashboard.lastSeenCentinela';

const ROLE_CHIP = {
  super_admin_inside:     { label: 'Admin Supra',           color: '#5346E6' },
  admin_colegio:          { label: 'Admin Colegio',         color: '#0F6C4B' },
  directivo:              { label: 'Directivo',             color: '#7A2AB1' },
  encargado_convivencia:  { label: 'Encargado Convivencia', color: '#B97A0D' },
  orientador:             { label: 'Orientador',            color: '#0E5FB9' },
};

export default function DashboardLayout() {
  const location = useLocation();
  const { profile } = useAuth();
  const modo = location.pathname.includes('/induccion') ? 'induccion' : 'oficial';
  const [nuevosCentinela, setNuevosCentinela] = useState(0);
  const [criteriosOpen, setCriteriosOpen] = useState(false);

  const roleChip = ROLE_CHIP[profile?.rol];
  const alcance = profile?.es_inside_staff
    ? 'Alcance INSIDE'
    : (profile?.colegio_id ? 'Tu colegio' : '');

  // Solo se consulta en modo oficial (inducción NO usa campanita crítica).
  // El hook maneja cache 30s + revalidación en segundo plano.
  const isOficial = modo === 'oficial';
  const qEventos = useEventosCentinela();
  const eventosCentinela = isOficial ? (qEventos.data?.casos || []) : [];
  const eventosLoading = isOficial ? qEventos.isLoading : false;

  useEffect(() => {
    if (!isOficial) { setNuevosCentinela(0); return; }
    const lastSeen = localStorage.getItem(STORAGE_KEY);
    const lastSeenDate = lastSeen ? new Date(lastSeen) : new Date(0);
    const nuevos = eventosCentinela.filter(c => new Date(c.creado_en) > lastSeenDate).length;
    setNuevosCentinela(nuevos);
  }, [eventosCentinela, isOficial]);

  const marcarVistoCentinela = () => {
    localStorage.setItem(STORAGE_KEY, new Date().toISOString());
    const eran = nuevosCentinela;
    setNuevosCentinela(0);
    track('centinela_visto', { previos: Math.min(eran, 20) });
  };
  const recargarEventosCentinela = () => qEventos.refetch();

  return (
    <div className="min-h-screen bg-[#F4F7FE]" data-testid="dashboard-layout">
      {/* Encabezado del Dashboard */}
      <header className="bg-white border-b border-[#E5EAF5] sticky top-0 z-30" data-testid="dashboard-header">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <Link to="/admin" className="flex items-center gap-2 text-[#475472] hover:text-[#0B1B5E] text-sm" data-testid="back-to-admin">
              <ArrowLeft className="w-4 h-4" /> Volver
            </Link>
            <div className="hidden sm:flex items-center gap-2 pl-3 border-l border-[#E5EAF5]">
              <ShieldCheck className="w-5 h-5 text-[#5346E6]" />
              <h1 className="font-extrabold text-[#0B1B5E]" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                Dashboard
              </h1>
              {roleChip && (
                <span
                  className="ml-2 text-[10px] font-bold px-2 py-0.5 rounded-full"
                  style={{ background: roleChip.color + '15', color: roleChip.color }}
                  title={alcance ? `${roleChip.label} · ${alcance}` : roleChip.label}
                  data-testid="dashboard-role-chip"
                >
                  {roleChip.label}
                  {alcance && <span className="opacity-70"> · {alcance}</span>}
                </span>
              )}
            </div>
          </div>

          {/* Toggle Oficial | Inducción */}
          <nav className="flex bg-[#F4F7FE] rounded-full p-1 border border-[#E5EAF5]" data-testid="dashboard-mode-toggle" aria-label="Modo del dashboard">
            <NavLink
              to="/admin/dashboard"
              end
              className={({ isActive }) =>
                `flex items-center gap-2 px-3 sm:px-4 py-1.5 text-xs sm:text-sm rounded-full font-bold transition ${
                  isActive ? 'bg-white text-[#0B1B5E] shadow-sm' : 'text-[#475472] hover:text-[#0B1B5E]'
                }`
              }
              data-testid="mode-oficial"
            >
              <ShieldCheck className="w-4 h-4" />
              Oficial
            </NavLink>
            <NavLink
              to="/admin/dashboard/induccion"
              className={({ isActive }) =>
                `flex items-center gap-2 px-3 sm:px-4 py-1.5 text-xs sm:text-sm rounded-full font-bold transition ${
                  isActive ? 'bg-white text-[#0F6C4B] shadow-sm' : 'text-[#475472] hover:text-[#0F6C4B]'
                }`
              }
              data-testid="mode-induccion"
            >
              <GraduationCap className="w-4 h-4" />
              Inducción
            </NavLink>
          </nav>

          <div className="flex items-center gap-2 sm:gap-3">
            <button
              type="button"
              onClick={() => setCriteriosOpen(true)}
              className="hidden md:flex items-center gap-1.5 text-xs text-[#475472] hover:text-[#0B1B5E] px-2 py-1.5 rounded-lg hover:bg-[#F4F7FE]"
              data-testid="btn-criterios-priorizacion"
              aria-label="Criterios de priorización"
            >
              <HelpCircle className="w-4 h-4" />
              Criterios
            </button>

            <button
              type="button"
              onClick={marcarVistoCentinela}
              className="relative p-2 rounded-full hover:bg-[#F4F7FE]"
              aria-label={nuevosCentinela > 0 ? `${nuevosCentinela} nuevos eventos centinela sin revisar` : 'Sin nuevos eventos centinela'}
              title={nuevosCentinela > 0
                ? `${nuevosCentinela} evento${nuevosCentinela === 1 ? '' : 's'} centinela nuevo${nuevosCentinela === 1 ? '' : 's'} desde tu última revisión. Haz clic para marcarlos como vistos.`
                : 'Sin nuevos eventos centinela desde tu última revisión.'}
              data-testid="alert-bell"
            >
              <Bell className={`w-5 h-5 ${nuevosCentinela > 0 ? 'text-[#5346E6]' : 'text-[#475472]'}`} />
              {nuevosCentinela > 0 && (
                <span
                  className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#5346E6] text-white text-[10px] font-bold flex items-center justify-center"
                  data-testid="alert-bell-badge"
                >
                  {nuevosCentinela > 9 ? '9+' : nuevosCentinela}
                </span>
              )}
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6" data-testid="dashboard-main">
        <Outlet context={{
          marcarVistoCentinela,
          nuevosCentinela,
          eventosCentinela,
          eventosLoading,
          recargarEventosCentinela,
        }} />
      </main>

      <PriorityCriteriaModal open={criteriosOpen} onClose={() => setCriteriosOpen(false)} />
    </div>
  );
}
